import Service, { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { Dayjs } from 'dayjs';
import {
  BENI_COMPARISON_SETS,
  type ComparisonSet,
  type ComparisonSetWithoutOwn,
  MAPPING_TYPE,
} from 'frontend-utils/utils/constants/benchmark-insight';
import { CURRENCY_MAPPING } from 'frontend-utils/utils/constants/currency-mapping-constants';
import type { CustomLegend } from 'lumen/components/hotel/benchmark-insight/charts/legend-items/legend-item';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  BENI_COMPARISON_SET_REFERENCE_PERIOD_TRANSLATIONS,
  BENI_REFERENCE_PERIOD_SHORT_TRANSLATIONS,
  type Kpi,
  KPIS,
  NUMBER_FORMATS,
  NUMBER_STYLES,
  type NumberFormat,
  type NumberStyle,
  type ReferencePeriod,
  SUPPORTED_FORMAT_NUMBER_COMBINATIONS,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import type MappingCategoryBenchmark from 'lumen/utils/datamodels/mapping-category-benchmark';
import BenchmarkMappingCategory, {
  type Breakdown,
  type HierarchyItem,
} from 'lumen/utils/datamodels/mapping-category-benchmark';

// Valid formatNumber format and style combinations:
// - percentage default: 0.1234 => 12% or -0.1234 => -12%
// - percentage change: 0.1234 => +12% or -0.1234 => -12%
// - currency default: 1234.5678 => $1,234 or -1234.5678 => -$1,234
// - currency change: 1234.5678 => +$1,234 or -1234.5678 => -$1,234
// - ranking default: 7 => 7 (can never be negative)
// - ranking change: 7 => +7 or -7 => -7
// - index default: 1.3634 => 136 or 0.5464 => 54 (can never be negative)
type ValidNumberFormatAndStyle<
  T extends NumberFormat,
  U extends NumberStyle,
> = (T extends 'currency' | 'ranking' | 'percentage'
  ? U extends 'default' | 'change'
    ? true
    : false
  : true) &
  (T extends 'index' ? (U extends 'default' ? true : false) : true);

export default class BenchmarkInsightBeniServiceService extends Service {
  @service('benchmark-insight/beni-hotels')
  declare beniHotelsService: Services['benchmark-insight/beni-hotels'];
  @service declare currency: Services['currency'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];

  @tracked selectedSegmentFilter: string | null = null;

  get allowedComparisonSets(): ComparisonSetWithoutOwn[] {
    return this.beniHotelsService.availableComparisonSets;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get beniCurrency() {
    return (
      this.currency.selectedCurrency ??
      this.hotelAndMarketService.hotel.benchmarkInsightCurrencyCode
    );
  }

  // In Benchmark Insight, "today is the future".
  // When there's no access to future data, the max end date should be yesterday.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get lastPastDate() {
    return this.hotelAndMarketService.getLocalTime().subtract(1, 'day');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get segmentFilter() {
    return this.selectedSegmentFilter;
  }

  addUnavailableComparisonSetDisabledTooltip(
    customLegends: CustomLegend[],
  ): void {
    const customLegendsWithDisabledTooltipText = customLegends.filter(
      (legend) =>
        legend.isDisabledForUnavailableComparisonSet &&
        legend.comparisonSetType === BENI_COMPARISON_SETS.OWN,
    );

    customLegendsWithDisabledTooltipText.forEach((legend) => {
      if (legend.isDisabledForUnavailableComparisonSet) {
        legend.disabledTooltipText = this.intl.t(
          'benchmark_insight.trial_without_data.chart_legend_tooltip',
        );
      }
    });
  }

  clearSegmentFilter(): void {
    this.selectedSegmentFilter = null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  createRootCategory(hierarchyItems: HierarchyItem[], breakdown: Breakdown) {
    // Create a model class for every category
    const isMappingByRateCode =
      this.hotelAndMarketService.hotel
        .benchmarkInsightMappingSegmentBreakdown === MAPPING_TYPE.RATE_CODE;
    const categories = hierarchyItems.map((hierarchyItem) =>
      BenchmarkMappingCategory.fromApiResponse(
        hierarchyItem,
        breakdown,
        isMappingByRateCode,
      ),
    );

    // Create a map to easily look up category models by id
    const categoriesById: { [id: string]: MappingCategoryBenchmark } = {};
    categories.forEach((category) => {
      categoriesById[category.id] = category;
    });

    // Add parent link
    const rootCategory = BenchmarkMappingCategory.createRootCategory(breakdown);
    categories.forEach((category) => {
      if (!category.isTopLevelCategory && category.parentId) {
        const parentCategory = categoriesById[category.parentId];

        if (parentCategory) {
          parentCategory.addChildCategory(category);
        }
      } else {
        rootCategory.addChildCategory(category);
      }
    });

    return rootCategory;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formatNumber<T extends NumberFormat, U extends NumberStyle>(
    value: number | null,
    numberFormat: T,
    numberStyle: U & ValidNumberFormatAndStyle<T, U> extends true ? U : never,
  ) {
    const currencyCode = this.beniCurrency;
    // BENI-TODO: Remove this check once all formatNumber calls have been converted to TypeScript.
    if (
      !(numberFormat in SUPPORTED_FORMAT_NUMBER_COMBINATIONS) ||
      !(numberStyle in SUPPORTED_FORMAT_NUMBER_COMBINATIONS[numberFormat])
    ) {
      this.errorTracking.captureException(
        `benchmarkService.formatNumber | Unsupported formatting combination used: ${numberFormat} + ${numberStyle}`,
        {},
      );
      return this.intl.t('general.missing');
    }

    // BENI-TODO: Remove this check once all formatNumber calls have been converted to TypeScript.
    if (value === undefined) {
      this.errorTracking.captureException(
        'benchmarkService.formatNumber | value is undefined',
        {},
      );
      return this.intl.t('general.missing');
    }

    // Null values in API should be formatted as general.missing.
    if (value === null) {
      return this.intl.t('general.missing');
    }

    const PERCENTAGE_DECIMAL_PLACES = 1;

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    function handleExceptZeroEdgeCase(valueToFormat: number) {
      // Edge case: we never want to have a sign for 0 values.
      // The intl.formatNumber 'signDisplay: exceptZero' option catches some of these cases, but there are some other scenarios where we end up with a sign.
      // To handle these other cases, check that the rounded value is not -0 or +0.
      // If it is: remove the sign before passing it to intl.formatNumber, by making it 0.
      let roundedValue = Math.round(valueToFormat);

      if (numberFormat === NUMBER_FORMATS.PERCENTAGE) {
        // Multiply by 100 to get the percentage value (e.g. 0.0049 * 100 = 0.49 => rounded to 0 => will be caught by the exceptZero check)
        // Additionally multiply by 10 per decimal place (e.g. 0.0049 * 1000 = 4.9 => rounded to 5 => will not be caught by the exceptZero check)
        roundedValue = Math.round(
          valueToFormat * (100 * 10 ** PERCENTAGE_DECIMAL_PLACES),
        );
      }

      // The exceptZero check: 'roundedValue === 0' checks for -0 and +0, since 0 === -0 and 0 === +0 in JavaScript.
      if (roundedValue === 0) {
        return 0;
      }

      return valueToFormat;
    }

    let valueToFormat = value;

    // Index values (such as ARI, MPI or RGI) should be multiplied by 100 and formatted as percentage, but without a % sign.
    if (numberFormat === NUMBER_FORMATS.INDEX) {
      valueToFormat = this.roundIndex(valueToFormat);
    }

    // Determine the style
    let style: 'percent' | 'currency' | 'decimal' = 'percent';

    if (numberFormat === NUMBER_FORMATS.CURRENCY) {
      style = 'currency';
    } else if (
      numberFormat === NUMBER_FORMATS.RANKING ||
      numberFormat === NUMBER_FORMATS.INDEX
    ) {
      style = 'decimal';
    }

    // Determine the signDisplay
    let signDisplay: 'auto' | 'never' | 'exceptZero' = 'auto';

    // signDisplay: 'negative' throws an error on Firefox.
    // 'negative' only shows a minus sign when the value is negative.
    // Instead of 'negative', we use 'auto' and correct it to 'never' when value is 0.
    if (valueToFormat === 0) {
      signDisplay = 'never';
    } else if (numberStyle === NUMBER_STYLES.CHANGE) {
      signDisplay = 'exceptZero';
    }

    // Remove sign from -0 or +0 if necessary before passing the value to intl.formatNumber.
    valueToFormat = handleExceptZeroEdgeCase(valueToFormat);

    // Round percentages to 1 decimal place, and all other numbers to 0 decimal places.
    const fractionDigits =
      numberFormat === NUMBER_FORMATS.PERCENTAGE
        ? PERCENTAGE_DECIMAL_PLACES
        : 0;

    let formattedNumber = this.intl.formatNumber(valueToFormat, {
      style,
      currency: currencyCode ?? undefined,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
      signDisplay,
    });

    // In English locale, intl chooses to display 'AED' instead of the symbol anyway. Manually override this by replacing it with the symbol.
    if (
      numberFormat === NUMBER_FORMATS.CURRENCY &&
      formattedNumber.includes(currencyCode)
    ) {
      formattedNumber = formattedNumber.replace(
        currencyCode,
        CURRENCY_MAPPING[currencyCode],
      );
    }

    // Display 'pt' instead of '%' for percentage change values to align with Revenue Insight.
    if (
      numberFormat === NUMBER_FORMATS.PERCENTAGE &&
      numberStyle === NUMBER_STYLES.CHANGE
    ) {
      formattedNumber = formattedNumber.replace('%', 'pt');
    }

    return formattedNumber;
  }

  // Index values in the API response need to be multiplied by 100 to get the percentage value.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getComparisonSetReferencePeriodShortTranslation(
    comparisonSet: ComparisonSet,
    referencePeriod: ReferencePeriod,
  ) {
    return this.intl.t(
      BENI_COMPARISON_SET_REFERENCE_PERIOD_TRANSLATIONS[comparisonSet],
      {
        referencePeriod: this.intl.t(
          BENI_REFERENCE_PERIOD_SHORT_TRANSLATIONS[referencePeriod],
        ),
      },
    );
  }

  getReferencePeriodDateRange(
    referencePeriod: ReferencePeriod,
    currentPeriodDateRange: { end: Dayjs; start: Dayjs },
  ): { end: Dayjs; start: Dayjs } {
    const currentPeriodStart = currentPeriodDateRange.start;
    const sameDateLastYear = currentPeriodStart.subtract(1, 'year');

    // To calculate the same day of the week last year, set the week of the year, and then set the day of the week.
    // We need to use `isoWeek` and `isoWeekday` to make sure Monday is the first day of the week.
    const currentPeriodWeekNumber = currentPeriodStart.isoWeek();
    const currentPeriodDayOfWeekNumber = currentPeriodStart.isoWeekday();
    const sameWeekLastYear = currentPeriodStart

      .subtract(1, 'year')
      .isoWeek(currentPeriodWeekNumber);
    const sameDayOfWeekLastYear = sameWeekLastYear.isoWeekday(
      currentPeriodDayOfWeekNumber,
    );

    // We want the reference period to be as long as the current period.
    // Therefore, calculate the difference in days between the current period start and end dates,
    // and use the same difference for the reference period date range.
    const differenceInDays = currentPeriodDateRange.end.diff(
      currentPeriodDateRange.start,
      'day',
    );

    const START_DATE_BY_REFERENCE_PERIOD = {
      stly: sameDayOfWeekLastYear,
      sdly: sameDateLastYear,
    };

    const startDate = START_DATE_BY_REFERENCE_PERIOD[referencePeriod];

    return {
      start: startDate,
      end: startDate.add(differenceInDays, 'day'),
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  isFutureRange(fromDate: Dayjs) {
    const { lastPastDate } = this;

    return fromDate.isAfter(lastPastDate, 'day');
  }

  // Returns whether the data is future compset data.
  // For legal reasons, we can't expose future compset ADR & RevPAR.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  isHiddenFutureCompsetData(
    comparisonSet: ComparisonSet,
    fromDate: Dayjs,
    kpi: Kpi,
  ) {
    const isFuture = this.isFutureRange(fromDate);

    // Check that we're showing compset data.
    const isCompset = comparisonSet === BENI_COMPARISON_SETS.COMPSET;

    // Check that we're showing ADR or RevPAR.
    const isAdrOrRevpar = kpi === KPIS.ADR || kpi === KPIS.REVPAR;

    return isFuture && isCompset && isAdrOrRevpar;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  isPastRange(toDate: Dayjs) {
    const { lastPastDate } = this;
    if (!toDate) {
      return false;
    }
    return (
      toDate.isBefore(lastPastDate, 'day') || toDate.isSame(lastPastDate, 'day')
    );
  }

  // They are also rounded to 0 decimal places.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  roundIndex(index: number) {
    return Math.round(index * 100);
  }

  setSegmentFilter(newValue: string): void {
    if (this.selectedSegmentFilter && this.selectedSegmentFilter === newValue) {
      this.clearSegmentFilter();
    } else {
      this.selectedSegmentFilter = newValue;
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'benchmark-insight/beni-service': BenchmarkInsightBeniServiceService;
  }
}
